import React, { useState, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import moment from "moment";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LogoutIcon from '@mui/icons-material/Logout';
import AvTimerIcon from '@mui/icons-material/AvTimer';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TodayIcon from '@mui/icons-material/Today';

import "./GetMyAttendance.css";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";
import StatsCard from "../common/StatsCard";
import {
  Typography,
  Paper,
  Chip,
  Grid,
} from "@mui/material";

import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const AttendanceRecords = () => {
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [error, setError] = useState("");
  const [month, setMonth] = useState(moment().month());
  const [year, setYear] = useState(moment().year());
  const [hasClickedDate, setHasClickedDate] = useState(false);

  useEffect(() => {
    const fetchAttendanceRecords = async () => {
      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          setError("No token found, please log in.");
          return;
        }

        const response = await fetch(
          `${BASE_URL}/api/attendance/attendancerecords`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || "Error fetching attendance records"
          );
        }

        const data = await response.json();
        setAttendanceRecords(data);
        setError("");
      } catch (err) {
        console.error(err);
        setError("Error fetching attendance records");
      }
    };

    fetchAttendanceRecords();
  }, []);

  useEffect(() => {
    filterRecords(attendanceRecords, month, year);
  }, [attendanceRecords, month, year]);

  const filterRecords = (records, month, year) => {
    const filtered = records.filter((record) => {
      const recordDate = moment(record.checkInTime);
      return recordDate.month() === month && recordDate.year() === year;
    });
    setFilteredRecords(filtered);
  };

  const handleMonthChange = (e) => {
    const newMonth = Number(e.target.value);
    setMonth(newMonth);
    setSelectedDate(new Date(year, newMonth, 1));
    setSelectedRecord(null);
    setHasClickedDate(false);
  };

  const handleYearChange = (e) => {
    const newYear = Number(e.target.value);
    setYear(newYear);
    setSelectedDate(new Date(newYear, month, 1));
    setSelectedRecord(null);
    setHasClickedDate(false);
  };

  // New handler for when the calendar view changes (arrow navigation)
  const handleActiveStartDateChange = ({ activeStartDate }) => {
    const newMonth = activeStartDate.getMonth();
    const newYear = activeStartDate.getFullYear();
    setMonth(newMonth);
    setYear(newYear);
    setSelectedDate(activeStartDate);
    setSelectedRecord(null);
    setHasClickedDate(false);
  };

  const formatDate = (date) => moment(date).format("YYYY-MM-DD");

  const handleDateClick = (date) => {
    setHasClickedDate(true);
    const formattedDate = formatDate(date);
    const record = filteredRecords.find(
      (record) => formatDate(record.checkInTime) === formattedDate
    );
    setSelectedRecord(record || null);
    setSelectedDate(date);
  };

  const getTileClassName = ({ date, view }) => {
    if (view !== "month") return null;

    const formattedDate = formatDate(date);
    const record = filteredRecords.find(
      (record) => formatDate(record.checkInTime) === formattedDate
    );
    const today = moment().startOf("day");
    const dateMoment = moment(date);

    // Mark Sundays with yellow background
    if (dateMoment.day() === 0) {
      return "sunday-date";
    }

    // Skip coloring for future dates (including today if it's in the future)
    if (dateMoment.isAfter(today)) {
      return "future-date";
    }

    // If we have a record for this date, mark as present
    if (record) return "present-date";

    // Only mark as absent if it's a weekday before today
    if (dateMoment.isBefore(today)) {
      return "absent-date";
    }

    return null;
  };

  const currentYear = moment().year();
  const years = Array.from(
    { length: currentYear - 2024 + 1 },
    (_, i) => 2024 + i
  );

  const formatWorkingHours = (workingHours) => {
    if (!workingHours) return "N/A";
    const { hours, minutes, seconds } = workingHours;
    return `${hours}h ${minutes}m ${seconds}s`;
  };

  const formatISTDateTime = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const presentDays = filteredRecords.length;
  const totalWeekdays = (() => {
    const start = moment([year, month, 1]);
    const end = moment.min(moment(start).endOf("month"), moment());
    let count = 0;
    const d = start.clone();
    while (d.isSameOrBefore(end, "day")) {
      if (d.day() !== 0) count++;
      d.add(1, "day");
    }
    return count;
  })();
  const absentDays = Math.max(0, totalWeekdays - presentDays);

  return (
    <PageContainer>
      <PageHeader
        gradient
        title="My Attendance"
        subtitle="Track your daily attendance, check-in and check-out records"
        icon={<CalendarMonthIcon />}
      />

      {error && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: "16px",
            border: "1px solid #FCA5A5",
            bgcolor: "#FEF2F2",
          }}
        >
          <Typography sx={{ color: "#991B1B", fontSize: "0.875rem" }}>
            {error}
          </Typography>
        </Paper>
      )}

      {/* Stats Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <StatsCard
            label="Present Days"
            value={presentDays}
            icon={<EventAvailableIcon />}
            color="#059669"
            bgColor="#D1FAE5"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatsCard
            label="Absent Days"
            value={absentDays}
            icon={<EventBusyIcon />}
            color="#DC2626"
            bgColor="#FEE2E2"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatsCard
            label="Working Days"
            value={totalWeekdays}
            icon={<TodayIcon />}
            color="#667eea"
            bgColor="#EBF0FF"
          />
        </Grid>
      </Grid>

      {/* Month/Year Selectors */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: "16px",
          border: "1px solid #eef0f6",
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "1rem",
            color: "#1a1a2e",
            mb: 2,
            pb: 1.5,
            borderBottom: "2px solid #f0f0f5",
          }}
        >
          Select Period
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="month-label">Month</InputLabel>
            <Select
              labelId="month-label"
              id="month"
              value={month}
              label="Month"
              onChange={handleMonthChange}
              sx={{ borderRadius: "8px", fontSize: "0.875rem" }}
            >
              {Array.from({ length: 12 }, (_, i) => (
                <MenuItem key={i} value={i} sx={{ fontSize: "0.875rem" }}>
                  {moment().month(i).format("MMMM")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="year-label">Year</InputLabel>
            <Select
              labelId="year-label"
              id="year"
              value={year}
              label="Year"
              onChange={handleYearChange}
              sx={{ borderRadius: "8px", fontSize: "0.875rem" }}
            >
              {years.map((y) => (
                <MenuItem key={y} value={y} sx={{ fontSize: "0.875rem" }}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Calendar + Details Two-Column Layout */}
      <Grid container spacing={3}>
        {/* Left: Calendar */}
        <Grid item xs={12} md={7}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid #eef0f6",
              p: 3,
              overflow: "hidden",
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "1rem",
                color: "#1a1a2e",
                mb: 2,
                pb: 1.5,
                borderBottom: "2px solid #f0f0f5",
              }}
            >
              Attendance Calendar
            </Typography>
            <div className="calendar-wrapper">
              <Calendar
                onChange={handleDateClick}
                tileClassName={getTileClassName}
                value={selectedDate}
                activeStartDate={new Date(year, month, 1)}
                minDate={new Date(2024, 0, 1)}
                maxDate={moment().endOf("day").toDate()}
                view="month"
                locale="en-US"
                showNeighboringMonth={false}
                calendarType="gregory"
                showFixedNumberOfWeeks={true}
                onActiveStartDateChange={handleActiveStartDateChange}
              />
            </div>
          </Paper>
        </Grid>

        {/* Right: Selected Day Details */}
        <Grid item xs={12} md={5}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid #eef0f6",
              p: 3,
              minHeight: 320,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "1rem",
                color: "#1a1a2e",
                mb: 2,
                pb: 1.5,
                borderBottom: "2px solid #f0f0f5",
              }}
            >
              Day Details
            </Typography>

            {hasClickedDate && !selectedRecord ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    color: "#8b8fa3",
                    fontSize: "0.875rem",
                    textAlign: "center",
                  }}
                >
                  No attendance record for{" "}
                  {moment(selectedDate).format("MMMM D, YYYY")}
                </Typography>
              </Box>
            ) : selectedRecord ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.9375rem",
                    color: "#1a1a2e",
                  }}
                >
                  {moment(selectedDate).format("MMMM D, YYYY")}
                </Typography>

                {/* Check-In */}
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    bgcolor: "#F0FDF4",
                    border: "1px solid #BBF7D0",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <AccessTimeIcon sx={{ fontSize: 18, color: "#059669" }} />
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.8125rem",
                        color: "#065F46",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Check-In
                    </Typography>
                    {selectedRecord.checkInSource === "outside" && (
                      <Chip
                        label="Outside"
                        size="small"
                        sx={{
                          bgcolor: "#FEF3C7",
                          color: "#92400E",
                          fontWeight: 600,
                          fontSize: "0.6875rem",
                          height: 22,
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    sx={{ fontSize: "0.875rem", color: "#1a1a2e", ml: 3.5 }}
                  >
                    {formatISTDateTime(selectedRecord.checkInTime)}
                  </Typography>
                  {selectedRecord.checkInSource === "outside" &&
                    (selectedRecord.checkInLocation ||
                      selectedRecord.checkInReason) && (
                      <Typography
                        sx={{
                          fontSize: "0.8125rem",
                          color: "#8b8fa3",
                          ml: 3.5,
                          mt: 0.5,
                        }}
                      >
                        {selectedRecord.checkInLocation &&
                          `Location: ${selectedRecord.checkInLocation}`}
                        {selectedRecord.checkInLocation &&
                          selectedRecord.checkInReason &&
                          " | "}
                        {selectedRecord.checkInReason &&
                          `Reason: ${selectedRecord.checkInReason}`}
                      </Typography>
                    )}
                </Box>

                {/* Check-Out */}
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    bgcolor: "#FEF2F2",
                    border: "1px solid #FECACA",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <LogoutIcon sx={{ fontSize: 18, color: "#DC2626" }} />
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.8125rem",
                        color: "#991B1B",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Check-Out
                    </Typography>
                    {selectedRecord.checkOutSource === "outside" && (
                      <Chip
                        label="Outside"
                        size="small"
                        sx={{
                          bgcolor: "#FEF3C7",
                          color: "#92400E",
                          fontWeight: 600,
                          fontSize: "0.6875rem",
                          height: 22,
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    sx={{ fontSize: "0.875rem", color: "#1a1a2e", ml: 3.5 }}
                  >
                    {selectedRecord.checkOutTime
                      ? formatISTDateTime(selectedRecord.checkOutTime)
                      : "Not checked out yet"}
                  </Typography>
                  {selectedRecord.checkOutSource === "outside" &&
                    (selectedRecord.checkOutLocation ||
                      selectedRecord.checkOutReason) && (
                      <Typography
                        sx={{
                          fontSize: "0.8125rem",
                          color: "#8b8fa3",
                          ml: 3.5,
                          mt: 0.5,
                        }}
                      >
                        {selectedRecord.checkOutLocation &&
                          `Location: ${selectedRecord.checkOutLocation}`}
                        {selectedRecord.checkOutLocation &&
                          selectedRecord.checkOutReason &&
                          " | "}
                        {selectedRecord.checkOutReason &&
                          `Reason: ${selectedRecord.checkOutReason}`}
                      </Typography>
                    )}
                </Box>

                {/* Working Hours */}
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    bgcolor: "#EFF6FF",
                    border: "1px solid #BFDBFE",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <AvTimerIcon sx={{ fontSize: 18, color: "#667eea" }} />
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.8125rem",
                        color: "#5a6fd8",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Working Hours
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "1.125rem",
                      fontWeight: 700,
                      color: "#1a1a2e",
                      ml: 3.5,
                    }}
                  >
                    {formatWorkingHours(selectedRecord.workingHours)}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    color: "#8b8fa3",
                    fontSize: "0.875rem",
                    textAlign: "center",
                  }}
                >
                  Select a date to view attendance details
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </PageContainer>
  );
};

export default AttendanceRecords;
